/**
 * Plattform-uavhengig flis-cache for kartlag.
 *
 * Hvorfor ikke bare service worker: SW kjører upålitelig/ikke i iOS WKWebView,
 * så ambient-caching av kartfliser slutter å virke på iPhone/iPad. Denne motoren
 * kjører i selve siden (via CachedTileLayer i Leaflet) og virker derfor identisk
 * på web, Android og iOS.
 *
 * Én delt Cache API-store betjener ALLE tre bruksmønstrene:
 *   - ambient: fliser du nettopp så på (cache-first, ingen ny henting ved re-visning)
 *   - offline-nedlasting: navngitte områder brukeren lagret bevisst (pinnes → aldri auto-evictet)
 *   - wifi-prefetch: fliser rundt viewport hentet på forhånd på umålt nett
 *
 * LRU-metadata (størrelse + sist brukt + pinnet) ligger i en injiserbar MetaStore
 * (IndexedDB i prod, in-memory i test). Cache API-en selv eksponerer verken
 * størrelse eller rekkefølge, så vi må spore det ved siden av.
 */

export const TILE_CACHE_NAME = 'fricamp-tiles-v1';

/**
 * Myktak for AMBIENT/prefetch-fliser (ikke pinnede nedlastinger). Over dette
 * evictes eldste ikke-pinnede fliser ned til EVICTION_TARGET_RATIO. Det
 * effektive taket er min(dette, QUOTA_FRACTION × enhetens kvote), så en
 * telefon med lite ledig plass aldri fylles opp.
 */
export const DEFAULT_SOFT_CAP_BYTES = 300 * 1024 * 1024; // 300 MB
const EVICTION_TARGET_RATIO = 0.9;
/** Ambient-cachen tar aldri mer enn denne andelen av total lagringskvote. */
const QUOTA_FRACTION = 0.5;

export interface TileMeta {
  url: string;
  size: number; // bytes
  lastUsed: number; // epoch ms
  pinned: boolean; // del av navngitt offline-område → aldri auto-evictet
  weight?: number; // «hotness» fra frekvens-cellen → beskytter mot LRU-eviction (se WEIGHT_BONUS_MS)
}

/**
 * Hvert vektpoeng (fra regionUsageService, 0..MAX_WEIGHT) teller som så mye ekstra
 * «nylig brukt» ved eviction. Fliser i områder du bruker mye aldres dermed
 * langsommere ut av ambient-cachen enn engangs-fliser med samme faktiske lastUsed.
 */
const WEIGHT_BONUS_MS = 12 * 60 * 60 * 1000; // 12 t per vektpoeng

export interface MetaStore {
  get(url: string): Promise<TileMeta | undefined>;
  put(meta: TileMeta): Promise<void>;
  delete(url: string): Promise<void>;
  all(): Promise<TileMeta[]>;
  /**
   * Oppdater lastUsed/weight for mange fliser i ÉN transaksjon. Valgfri: stores
   * uten den faller tilbake til get+put per URL (se `flushTouches`).
   */
  touchMany?(entries: { url: string; lastUsed: number; weight?: number }[]): Promise<void>;
}

export interface TileCacheDeps {
  cacheStorage?: CacheStorage;
  fetchImpl?: typeof fetch;
  metaStore?: MetaStore;
  softCapBytes?: number;
  /** For test: overstyr persist-forsøket. */
  requestPersist?: () => Promise<boolean>;
  /** For test: overstyr kvote-estimatet (default navigator.storage.estimate). */
  estimateStorage?: () => Promise<{ quota?: number; usage?: number }>;
}

// --- Injiserbar konfigurasjon (defaults settes lazily i prod) ---

let _cacheStorage: CacheStorage | undefined;
let _fetchImpl: typeof fetch | undefined;
let _metaStore: MetaStore | undefined;
let _softCap = DEFAULT_SOFT_CAP_BYTES;
let _requestPersist: (() => Promise<boolean>) | undefined;
let _estimateStorage: (() => Promise<{ quota?: number; usage?: number }>) | undefined;
let _persistRequested = false;

// Løpende sum av IKKE-pinnede bytes. Utledes ÉN gang fra MetaStore (ensureTotals)
// og vedlikeholdes deretter inkrementelt ved hver put/delete/unpin/evict — slik at
// eviction-sjekken blir O(1) i stedet for en full-tabell-skann per flis.
let _unpinnedBytes: number | null = null;
let _totalsLoad: Promise<void> | null = null;

// Cachet effektivt tak (estimateStorage kan være dyrt) — kort levetid.
let _capValue: number | null = null;
let _capAt = 0;
const CAP_TTL_MS = 60_000;

export function configureTileCache(deps: TileCacheDeps): void {
  if (deps.cacheStorage) _cacheStorage = deps.cacheStorage;
  if (deps.fetchImpl) _fetchImpl = deps.fetchImpl;
  if (deps.metaStore) _metaStore = deps.metaStore;
  if (typeof deps.softCapBytes === 'number') _softCap = deps.softCapBytes;
  if (deps.requestPersist) _requestPersist = deps.requestPersist;
  if (deps.estimateStorage) _estimateStorage = deps.estimateStorage;
  // Konfig kan endre tak/kvote/metastore → tving ny beregning.
  _capValue = null;
}

/** For test: nullstill all intern tilstand. */
export function __resetTileCacheForTests(): void {
  _cacheStorage = undefined;
  _fetchImpl = undefined;
  _metaStore = undefined;
  _softCap = DEFAULT_SOFT_CAP_BYTES;
  _requestPersist = undefined;
  _estimateStorage = undefined;
  _persistRequested = false;
  _unpinnedBytes = null;
  _totalsLoad = null;
  _capValue = null;
  _capAt = 0;
  inFlight.clear();
  pendingStores.clear();
  pendingTouches.clear();
  if (touchFlushTimer) {
    clearTimeout(touchFlushTimer);
    touchFlushTimer = null;
  }
}

/**
 * For test: vent på bakgrunns-lagringen som `getTile` starter etter at flisen er
 * returnert. I prod er dette bevisst «fire-and-forget» (av render-stien).
 */
export async function __flushTileStoresForTests(): Promise<void> {
  while (pendingStores.size) {
    await Promise.all([...pendingStores]);
  }
}

/**
 * Sørger for at den løpende ikke-pinnede summen er initialisert. Kjører kun én
 * gang (utleder fra MetaStore) — inkluderer fliser fra tidligere økter i IDB.
 */
async function ensureTotals(): Promise<void> {
  if (_unpinnedBytes !== null) return;
  if (!_totalsLoad) {
    _totalsLoad = (async () => {
      try {
        const all = await metaStore().all();
        _unpinnedBytes = all.reduce((s, m) => s + (m.pinned ? 0 : m.size || 0), 0);
      } catch {
        _unpinnedBytes = 0;
      }
    })();
  }
  await _totalsLoad;
}

/** Effektivt tak med kort cache (unngår estimateStorage-kall per flis). */
async function cachedEffectiveCap(): Promise<number> {
  const now = Date.now();
  if (_capValue !== null && now - _capAt < CAP_TTL_MS) return _capValue;
  _capValue = await effectiveCap();
  _capAt = now;
  return _capValue;
}

/** Effektivt ambient-tak: min(myktak, andel av enhetens ledige kvote). */
async function effectiveCap(): Promise<number> {
  try {
    const est = _estimateStorage
      ? await _estimateStorage()
      : await (globalThis.navigator as any)?.storage?.estimate?.();
    if (est?.quota && est.quota > 0) {
      return Math.min(_softCap, Math.floor(est.quota * QUOTA_FRACTION));
    }
  } catch {
    /* fall tilbake til fast myktak */
  }
  return _softCap;
}

function cacheStorage(): CacheStorage {
  const cs = _cacheStorage ?? (globalThis as any).caches;
  if (!cs) throw new Error('CacheStorage utilgjengelig');
  return cs;
}

function fetchImpl(): typeof fetch {
  return _fetchImpl ?? globalThis.fetch.bind(globalThis);
}

function metaStore(): MetaStore {
  if (!_metaStore) _metaStore = createIdbMetaStore();
  return _metaStore;
}

async function openCache(): Promise<Cache> {
  return cacheStorage().open(TILE_CACHE_NAME);
}

/** Ber nettleseren om vedvarende lagring én gang (kritisk mot iOS-eviction). */
export async function ensurePersistentStorage(): Promise<boolean> {
  if (_persistRequested) return false;
  _persistRequested = true;
  try {
    if (_requestPersist) return await _requestPersist();
    const storage = (globalThis.navigator as any)?.storage;
    if (storage?.persisted && (await storage.persisted())) return true;
    if (storage?.persist) return await storage.persist();
  } catch {
    /* stille — persist er en optimalisering, ikke et krav */
  }
  return false;
}

// --- Kjernen ---

const inFlight = new Map<string, Promise<Response>>();

// Bakgrunns-lagringer startet av getTile (av render-stien). Kun for test-flushing.
const pendingStores = new Set<Promise<unknown>>();
function trackStore(p: Promise<unknown>): void {
  pendingStores.add(p);
  void p.finally(() => pendingStores.delete(p));
}

/**
 * Henter en flis cache-first. Samtidige kall for samme URL koalesceres til én
 * nettverkshenting. Returnerer alltid en fersk (klonet) Response til kalleren.
 *
 * VIKTIG: ved cache-miss returneres flisen så snart nettverket svarer. Lagring +
 * eviction skjer i BAKGRUNNEN (fire-and-forget) — den kjeden lå tidligere synkront
 * på render-stien og skalerte O(n) med cache-størrelsen (full metabase-skann per
 * flis), som gjorde satellittkartet gradvis tregere jo mer cachen fyltes.
 */
export async function getTile(
  url: string,
  opts?: { signal?: AbortSignal; pin?: boolean; weight?: number },
): Promise<Response> {
  const cache = await openCache();
  const cached = await cache.match(url);
  if (cached) {
    touch(url, opts?.weight);
    return cached;
  }

  const existing = inFlight.get(url);
  if (existing) {
    const res = await existing;
    return res.clone();
  }

  const p = fetchImpl()(url, opts?.signal ? { signal: opts.signal } : undefined).finally(() =>
    inFlight.delete(url),
  );
  inFlight.set(url, p);
  const res = await p;
  if (res.ok) {
    // Klon FØR retur: originalen forblir uberørt så både lagring og retur får
    // hver sin lesbare kropp.
    trackStore(storeResponse(url, res.clone(), !!opts?.pin, opts?.weight).catch(() => {}));
  }
  return res.clone();
}

/**
 * Lagrer en allerede-hentet Response direkte i cachen. Brukes av
 * offline-nedlaster (pin=true) og prefetch (pin=false).
 */
export async function putTile(url: string, response: Response, pin = false): Promise<void> {
  if (!response.ok) return;
  await storeResponse(url, response, pin);
}

async function storeResponse(url: string, response: Response, pin: boolean, weight?: number): Promise<void> {
  // Ikke lagre service-workerens transparente fallback-flis (midlertidig nettfeil)
  // — ellers ville en blank flis bli hengende permanent i cachen.
  if (response.headers.get('x-fallback-tile') === '1') return;

  // Størrelse: bruk content-length (billig) og unngå blob()-roundtrip. Bare hvis
  // headeren mangler leser vi kroppen (klonet, så put-en under ikke tømmes).
  let size = 0;
  const len = Number(response.headers.get('content-length'));
  if (Number.isFinite(len) && len > 0) {
    size = len;
  } else {
    try {
      size = (await response.clone().blob()).size;
    } catch {
      size = 0;
    }
  }

  const cache = await openCache();
  const store = metaStore();
  await ensureTotals();
  const prev = await store.get(url);

  await cache.put(url, response);

  const pinned = pin || !!prev?.pinned; // én gang pinnet forblir pinnet
  const finalSize = size || prev?.size || 0;
  const finalWeight = Math.max(weight ?? 0, prev?.weight ?? 0); // behold høyeste hotness sett
  await store.put({ url, size: finalSize, lastUsed: Date.now(), pinned, weight: finalWeight });

  // Vedlikehold løpende ikke-pinnet sum inkrementelt.
  const prevContribution = prev && !prev.pinned ? prev.size || 0 : 0;
  const newContribution = pinned ? 0 : finalSize;
  _unpinnedBytes = (_unpinnedBytes ?? 0) + newContribution - prevContribution;

  await maybeEvict();
}

/**
 * LRU-oppdateringer som venter på å bli skrevet, samlet per URL.
 *
 * HVORFOR BATCHING: `touch` gjorde tidligere `store.get()` + `store.put()` — to
 * SEPARATE IndexedDB-transaksjoner — for HVER flis som ble truffet i cachen. Et
 * satellittkart på telefon har fort 200+ fliser i DOM (to lag × keepBuffer), så én
 * panorering utløste flere hundre IDB-transaksjoner. IndexedDB i Android WebView er
 * ikke gratis, og alt lå på samme tråd som kartet tegnes på. Del av
 * treghets-rapporten 23.08.2026.
 *
 * Nå samles treffene i minnet og skrives i ÉN transaksjon litt senere. LRU tåler at
 * `lastUsed` er noen sekunder unøyaktig — presisjonen betyr ingenting når terskelen
 * for eviction er hundrevis av megabyte.
 */
const pendingTouches = new Map<string, { lastUsed: number; weight?: number }>();
let touchFlushTimer: ReturnType<typeof setTimeout> | null = null;

/** Hvor lenge vi samler opp treff før de skrives. */
export const TOUCH_FLUSH_MS = 2000;

/**
 * Under dette er `lastUsed` fersk nok at en ny skriving ikke forteller oss noe. Uten
 * denne ville en bruker som panorerer fram og tilbake skrive samme flis om og om
 * igjen. Vekt-økninger slipper alltid gjennom (de kan gi eviction-beskyttelse).
 */
export const TOUCH_MIN_AGE_MS = 5 * 60 * 1000;

/** Skriver alle oppsamlede treff i én transaksjon. Best effort — LRU er ikke kritisk. */
export async function flushTouches(): Promise<void> {
  if (touchFlushTimer) {
    clearTimeout(touchFlushTimer);
    touchFlushTimer = null;
  }
  if (!pendingTouches.size) return;
  const batch = [...pendingTouches].map(([url, v]) => ({ url, ...v }));
  pendingTouches.clear();
  try {
    const store = metaStore();
    if (store.touchMany) {
      await store.touchMany(batch);
      return;
    }
    // Fallback for stores uten batch-støtte (in-memory i test).
    for (const entry of batch) {
      const meta = await store.get(entry.url);
      if (!meta) continue;
      await store.put({
        ...meta,
        lastUsed: entry.lastUsed,
        weight: Math.max(entry.weight ?? 0, meta.weight ?? 0),
      });
    }
  } catch {
    /* stille */
  }
}

/**
 * Køer en LRU-oppdatering. Synkron og allokeringsfri nok til å ligge på render-stien.
 */
function touch(url: string, weight?: number): void {
  const existing = pendingTouches.get(url);
  pendingTouches.set(url, {
    lastUsed: Date.now(),
    weight: Math.max(weight ?? 0, existing?.weight ?? 0),
  });
  if (!touchFlushTimer) {
    touchFlushTimer = setTimeout(() => {
      touchFlushTimer = null;
      void flushTouches();
    }, TOUCH_FLUSH_MS);
  }
}

export async function hasTile(url: string): Promise<boolean> {
  const cache = await openCache();
  return !!(await cache.match(url));
}

export async function deleteTile(url: string): Promise<void> {
  const cache = await openCache();
  const store = metaStore();
  await ensureTotals();
  const meta = await store.get(url);
  await cache.delete(url);
  await store.delete(url);
  if (meta && !meta.pinned) {
    _unpinnedBytes = Math.max(0, (_unpinnedBytes ?? 0) - (meta.size || 0));
  }
}

/** Fjerner pin fra en flis (kalles når et offline-område slettes). */
export async function unpinTile(url: string): Promise<void> {
  const store = metaStore();
  await ensureTotals();
  const meta = await store.get(url);
  if (meta?.pinned) {
    await store.put({ ...meta, pinned: false });
    // Avpinnet flis teller nå mot ambient-taket.
    _unpinnedBytes = (_unpinnedBytes ?? 0) + (meta.size || 0);
  }
}

export async function getTotalBytes(): Promise<number> {
  const all = await metaStore().all();
  return all.reduce((sum, m) => sum + (m.size || 0), 0);
}

/**
 * Evicter minst-nylig-brukte, IKKE-pinnede fliser til ambient-forbruket er under
 * det effektive taket. Pinnede fliser (bevisst lagrede offline-områder) røres
 * aldri, og teller heller ikke mot ambient-taket — de vises separat i
 * offline-panelet som brukerens eget valg.
 */
export async function maybeEvict(): Promise<number> {
  await ensureTotals();
  const cap = await cachedEffectiveCap();
  // Rask vei (det normale): under taket → ingen full-tabell-skann.
  if ((_unpinnedBytes ?? 0) <= cap) return 0;

  // Over taket → nå (og bare nå) tar vi den dyre LRU-skanningen.
  const store = metaStore();
  const all = await store.all();
  // Effektiv «sist brukt» = faktisk lastUsed + hotness-bonus. Fliser i områder du
  // bruker mye evictes dermed sist, selv om de faktisk ble sett for en stund siden.
  const effLastUsed = (m: TileMeta) => m.lastUsed + (m.weight ?? 0) * WEIGHT_BONUS_MS;
  const unpinned = all
    .filter((m) => !m.pinned)
    .sort((a, b) => effLastUsed(a) - effLastUsed(b)); // «kaldest» først

  const target = Math.floor(cap * EVICTION_TARGET_RATIO);
  const cache = await openCache();
  let evicted = 0;
  for (const m of unpinned) {
    if ((_unpinnedBytes ?? 0) <= target) break;
    await cache.delete(m.url);
    await store.delete(m.url);
    _unpinnedBytes = Math.max(0, (_unpinnedBytes ?? 0) - (m.size || 0));
    evicted++;
  }
  return evicted;
}

// --- IndexedDB-basert MetaStore (prod-default) ---

const IDB_NAME = 'fricamp-tilecache-meta';
const IDB_STORE = 'tiles';

function createIdbMetaStore(): MetaStore {
  let dbPromise: Promise<IDBDatabase> | null = null;

  const open = (): Promise<IDBDatabase> => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
      const req = globalThis.indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) {
          db.createObjectStore(IDB_STORE, { keyPath: 'url' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return dbPromise;
  };

  const tx = async <T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> => {
    const db = await open();
    return new Promise<T>((resolve, reject) => {
      const t = db.transaction(IDB_STORE, mode);
      const req = fn(t.objectStore(IDB_STORE));
      req.onsuccess = () => resolve(req.result as T);
      req.onerror = () => reject(req.error);
    });
  };

  return {
    get: (url) => tx<TileMeta | undefined>('readonly', (s) => s.get(url)),
    put: async (meta) => {
      await tx('readwrite', (s) => s.put(meta));
    },
    delete: async (url) => {
      await tx('readwrite', (s) => s.delete(url));
    },
    all: () => tx<TileMeta[]>('readonly', (s) => s.getAll()),
    /**
     * Alle oppdateringene i ÉN readwrite-transaksjon. Erstatter get+put per flis
     * (to transaksjoner × flere hundre fliser per panorering — se `flushTouches`).
     *
     * Fliser som allerede har en fersk `lastUsed` og ingen høyere vekt hoppes over
     * helt: skrivingen ville ikke endret noe LRU bryr seg om.
     */
    touchMany: async (entries) => {
      if (!entries.length) return;
      const db = await open();
      await new Promise<void>((resolve, reject) => {
        const t = db.transaction(IDB_STORE, 'readwrite');
        const store = t.objectStore(IDB_STORE);
        t.oncomplete = () => resolve();
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error);
        for (const entry of entries) {
          const req = store.get(entry.url);
          req.onsuccess = () => {
            const meta = req.result as TileMeta | undefined;
            if (!meta) return;
            const nextWeight = Math.max(entry.weight ?? 0, meta.weight ?? 0);
            const weightGrew = nextWeight > (meta.weight ?? 0);
            const stale = entry.lastUsed - (meta.lastUsed || 0) >= TOUCH_MIN_AGE_MS;
            if (!weightGrew && !stale) return;
            store.put({ ...meta, lastUsed: entry.lastUsed, weight: nextWeight });
          };
        }
      });
    },
  };
}
